import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { fromFlatConfig, toFlatConfig } from '../domain';
import { computeBaselinePricing } from '../utils/baselinePricing';
import { get } from '@aws-amplify/api';
import { ConsoleLogger } from 'aws-amplify/utils';
import { applyRulesFromDomain, buildContext } from '../domain/rules';
import { log } from '../utils/devLog';
import { getInitialConfig, getInitialMetaState, getInitialMetrics } from '../configEngine/initialConfig';
import { prepareLoadedConfig } from '../configEngine/loadedConfig';
import { deriveOutputs } from '../configEngine/deriveOutputs';

// Re-exported so the ~20 existing importers of getInitialConfig/getInitialMetaState
// from this module are untouched — the implementations now live in
// configEngine/initialConfig.js, which has no React/Amplify dependency so the
// MCP Lambda (CONF-112) can import it headlessly.
export { getInitialConfig, getInitialMetaState };

// Stable empty-object reference for the `customPrices` default. Using a fresh
// `{}` literal as a parameter default would allocate a new object on every
// call, defeating the effect's dependency check below (it would look "changed"
// on every render and re-run the debounced calculation loop even when nothing
// actually changed).
const EMPTY_CUSTOM_PRICES = {};

/**
* useConfigurationLogic Hook
* 
* Centralized configuration management for the V5000 system configurator.
* Handles all configuration state, business rules, and derived calculations.
* 
* @param {Object} productDb - Product database containing pricing and specifications
* @param {Object} metricsDb - Metrics database containing performance specifications
* @param {string} hardwareVendor - Hardware vendor ('AIC' or 'DELL')
* @param {Object} [options] - Additive options. { customPrices } - exec price overrides,
*   Record<partNumber, dollars>, HARDWARE SKUs only. Defaults to {} (catalog pricing, unchanged behavior).
* 
* @returns {Object} Configuration state and control functions:
*   - config: Current configuration object with all settings
*   - metrics: Calculated system metrics (capacity, performance, cost, physical)
*   - bom: Generated bill of materials array
*   - isCalculating: Boolean indicating if calculations are in progress
*   - updateConfig(updates): Update single/multiple config fields with business rule validation
*   - batchUpdateConfig(updates): Efficiently update multiple fields at once
*   - loadConfig(savedConfig): Load and migrate saved configuration from storage
* 
* Features:
* - Auto-applies business rules on every configuration change
* - Debounced expensive calculations (300ms delay after changes stop)
* - Handles pricing updates when quarter/service options change
* - Validates storage type requirements (at least one type must be enabled)
* - Migrates legacy configuration formats automatically
* - Memoized to prevent unnecessary re-calculations
*/
export const useConfigurationLogic = (productDb, metricsDb, vendor, options = {}) => {
  // customPrices: exec price overrides, Record<partNumber, dollars>, HARDWARE SKUs only.
  // Additive — existing callers that omit `options` keep today's catalog-only pricing.
  const { customPrices = EMPTY_CUSTOM_PRICES } = options;

  // Create Meta State Ref FIRST, before anything else
  const metaStateRef = useRef(getInitialMetaState());

  const initialConfig = useMemo(() => {
    return getInitialConfig(productDb, vendor.id);
  },[productDb, vendor]);

  const [domain, setDomain] = useState(() => fromFlatConfig(initialConfig));
  const config = useMemo(() => toFlatConfig(domain), [domain]);

  // MORK TODO: Investigate not applying business rules on initial load/config. We already get the default config

  const [calculations, setCalculations] = useState({
    metrics: getInitialMetrics(metricsDb),
    bom: [],
    isCalculating: false,
    lastCalculated: null
  });

  // Behavior-identical inline of the retired applyBusinessRules facade.
  const runRules = useCallback((newFlat, prevDomain) => {
    const ctx = buildContext(newFlat, productDb, metaStateRef.current, vendor);
    return applyRulesFromDomain(prevDomain, fromFlatConfig(newFlat), ctx);
  }, [productDb, vendor]);

  /**
   * Updates the configuration state by merging the provided updates with the previous state
   * and applying business rules to the resulting configuration.
   *
   * @function
   * @param {Object} updates - An object containing the updates to be applied to the configuration state.
   * @returns {void}
   */
  const updateConfig = useCallback((updates) => {
    setDomain(prevDomain => {
      const newConfig = { ...toFlatConfig(prevDomain), ...updates };
      return runRules(newConfig, prevDomain);
    });
  }, [runRules]);
  

  /**
   * Updates the configuration state with the provided updates and applies business rules.
   *
   * This function takes an object of updates, merges them into the current configuration state,
   * and then applies business rules to ensure the configuration remains valid.
   *
   * @function
   * @param {Object} updates - An object containing the configuration updates to apply.
   * @returns {void}
   * 
   * @example
   * batchUpdateConfig({ key1: 'value1', key2: 'value2' });
   */
  const batchUpdateConfig = useCallback((updates) => {
    setDomain(prevDomain => {
      let newConfig = { ...toFlatConfig(prevDomain) };

      // Apply all updates first
      Object.assign(newConfig, updates);

      // Then apply business rules once
      return runRules(newConfig, prevDomain);
    });
  }, [runRules]);


  /**
   * Loads and processes the configuration for the application.
   * 
   * This function handles the migration of the saved configuration, applies pricing logic,
   * and updates the configuration state. It also manages the meta state to indicate the 
   * loading process and pricing lock status.
   * 
   * @function
   * @param {Object} savedConfig - The saved configuration object to be loaded.
   * @returns {void}
   * 
   * @example
   * loadConfig(savedConfig);
   * 
   * @remarks
   * - If `ssdSoftware` and `hddSoftware` are present in the saved configuration, their values
   *   are preserved, and base prices are derived based on the `isNoReg` flag.
   * - If pricing is not present in the saved configuration, it is fetched from the product database
   *   based on the quarter and service option.
   * - Applies business rules to the new configuration before updating the state.
   * - Logs debug information if `REACT_APP_TEST_LOGS` is enabled.
   * 
   * @dependencies
   * - `prepareLoadedConfig` (configEngine/loadedConfig): migrates the saved
   *   configuration, merges it over the previous session, and derives its
   *   pricing — everything that must happen before business rules run.
   * - `applyBusinessRules`: Applies business rules to the configuration.
   *
   * @sideEffects
   * - Updates `metaStateRef` to manage loading and pricing lock status.
   * - Logs debug information to the console if enabled.
   */
  const loadConfig = useCallback((savedConfig) => {
    metaStateRef.current = {
      ...metaStateRef.current,
      isLoadingConfig: true,
      isPricingLocked: true,
      lastLoadedAt: new Date().toISOString()
    };

    log('Setting pricing lock for config load');

    setDomain(prevDomain => {
      const newConfig = prepareLoadedConfig(toFlatConfig(prevDomain), savedConfig, productDb, vendor);
      return runRules(newConfig, prevDomain);
    });


    // Update metaStateRef ref directly
    setTimeout(() => {
      metaStateRef.current = {
        ...metaStateRef.current,
        isPricingLocked: false,
        isLoadingConfig: false
      };

      log('Pricing lock released');
    }, 0);
  }, [productDb, vendor, runRules]);
  

  /*
    Calculate metrics and BOM when config changes. Calculations are debounced and begin after a 300ms delay after the last config change
  */
  useEffect(() => {
    setCalculations(prev => ({ ...prev, isCalculating: true }));
    
    const timer = setTimeout(() => {
      try {
        log('=== STARTING CALCULATIONS ===');
        log('Current Config:', config);

        const { metrics, bom, pricingData } = deriveOutputs({
          domain, config, productDb, metricsDb, vendor, customPrices,
        });

        // The "Current" (list-price) counterpart to the figures above. Null
        // unless an exec lever — a hardware override, a software price, or
        // discount months — is actually off list AND moves a total, so the
        // untouched path costs nothing and the Special Pricing card keeps its
        // plain single-value tiles.
        const baselinePricing = computeBaselinePricing({
          config, productDb, metrics, vendor, livePricing: pricingData, customPrices,
        });

        log('=== CALCULATIONS COMPLETE ===');
        setCalculations({
          metrics,
          bom,
          pricingData,
          baselinePricing,
          isCalculating: false,
          lastCalculated: new Date()
        });
      } catch (error) {
        console.error('Calculation error:', error);
        setCalculations(prev => ({
          ...prev,
          isCalculating: false,
          error: error.message
        }));
      }
    }, 300); // 300ms debounce
    
    return () => clearTimeout(timer);
  }, [domain, productDb, metricsDb, customPrices]);

  return {
    config,
    system: domain.system,
    pricing: domain.pricing,
    viewState: domain.viewState,
    metadata: domain.metadata,
    metrics: calculations.metrics,
    bom: calculations.bom,
    pricingData: calculations.pricingData,
    baselinePricing: calculations.baselinePricing,
    isCalculating: calculations.isCalculating,
    updateConfig,
    batchUpdateConfig,
    loadConfig
  };
};

// // Helper function for migration
// const migrateQuoteConfig = (savedConfig) => {
//   const needsMigration = savedConfig.hybridEncodingScheme &&
//     savedConfig.hybridEncodingScheme.includes('+') &&
//     savedConfig.hybridSpares === undefined;

//   if (needsMigration) {
//     const hybridParts = savedConfig.hybridEncodingScheme.split('+');
//     const afaParts = savedConfig.afaEncodingScheme?.split('+') || [];

//     if (hybridParts.length === 3) {
//       return {
//         ...savedConfig,
//         hybridEncodingScheme: `${hybridParts[0]}+${hybridParts[1]}`,
//         afaEncodingScheme: afaParts.length === 3 
//           ? `${afaParts[0]}+${afaParts[1]}` 
//           : (savedConfig.afaEncodingScheme || "4+2"),
//         hybridSpares: 2,
//         afaSpares: 2,
//         hybridMLEC: false
//       };
//     }
//   }

//   return {
//     ...savedConfig,
//     hybridSpares: savedConfig.hybridSpares ?? 2,
//     afaSpares: savedConfig.afaSpares ?? 2,
//     hybridMLEC: savedConfig.hybridMLEC ?? false
//   };
// };